import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import usePark from "common/hooks/usePark";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import Icon from "components/Icon/Icon";
import { Balance } from "types/Balance";
import { getParkConfiguration } from "types/Park";
import { BalanceSheetDisplayOptions } from "types/SavedBalanceSheet";
import DistributionRadar from "./DistributionChart";
import EvolutionChart from "./EvolutionChart";

type Props = {
  balance?: Balance;
  displayOptions: BalanceSheetDisplayOptions;
  /** The whole report is what `generatePDF` scrapes to build the PDF. */
  chartRef: React.RefObject<HTMLDivElement>;
};

function BalanceSheetReport({ balance, displayOptions, chartRef }: Props) {
  const { t } = useTranslation();
  const { park } = usePark();

  const parkConfiguration = useMemo(() => {
    if (park) {
      return getParkConfiguration(park);
    }
    return null;
  }, [park]);

  return (
    <div className="w-full" ref={chartRef}>
      <div className="rounded-t-2xl bg-slate-200 flex flex-row gap-4 flex-1 p-4">
        {balance?.park && balance?.park?.logo && (
          <div className="rounded-xl overflow-hidden flex items-center">
            <img
              src={ImageFromStrapiMedia(balance?.park?.logo)?.uri ?? undefined}
              className="max-h-32 w-auto"
              alt=""
            />
          </div>
        )}
        <div className="flex-1 flex flex-col justify-center items-center">
          <span className="font-semibold text-emerald-900">
            {t("balance.parkName")}
          </span>
          <p className="text-3xl font-bold text-emerald-950">
            {balance?.park?.name}
          </p>
        </div>
        <div className="p-6 flex flex-col justify-center items-center border-l-2 border-l-slate-300 flex-1">
          <span className="font-semibold text-emerald-900">
            {t("balance.period")}
          </span>
          <div className="flex flex-col gap-0">
            <p className="text-2xl font-bold text-emerald-950">
              {dayjs(balance?.startDate).format("LL")}
            </p>
            <div className="flex flex-row items-center gap-1">
              <Icon
                name="ChevronRightIcon"
                className="w-4 h-4 text-emerald-600"
              />
              <Icon
                name="ChevronRightIcon"
                className="w-4 h-4 text-emerald-600 -ml-3"
              />
              <p className="text-2xl font-bold text-emerald-950">
                {dayjs(balance?.endDate).format("LL")}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-b-2xl bg-slate-200 flex flex-col gap-4 flex-1">
        <div className="flex flex-row items-center flex-1">
          <div className="flex flex-row gap-4 p-4 flex-1">
            <div className="bg-white rounded-xl p-3 flex flex-col gap-1 relative flex-1">
              <Icon
                name="EyeIcon"
                className="w-6 h-6 absolute top-3 right-3 text-emerald-700/50"
              />
              <span className="text-3xl font-bold text-emerald-700">
                {balance?.global?.numberOfObservations ?? 0}
              </span>
              <span className="font-semibold leading-5 text-emerald-900">
                {t("balance.numberOfObservations")}
              </span>
            </div>

            <div className="bg-white rounded-xl p-3 flex flex-col gap-1 relative flex-1">
              <Icon
                name="IoPawOutline"
                className="w-6 h-6 absolute top-3 right-3 text-emerald-700/50"
              />
              <span className="text-3xl font-bold text-emerald-700">
                {balance?.global?.numberOfSpecies ?? 0}
              </span>
              <span className="font-semibold leading-5 text-emerald-900">
                {t("balance.numberOfSpecies")}
              </span>
            </div>

            <div className="bg-white rounded-xl p-3 flex flex-col gap-1 relative flex-1">
              <Icon
                name="UserIcon"
                className="w-6 h-6 absolute top-3 right-3 text-emerald-700/50"
              />
              <span className="text-3xl font-bold text-emerald-700">
                {balance?.global?.numberOfObservators ?? 0}
              </span>
              <span className="font-semibold leading-5 text-emerald-900">
                {t("balance.numberOfObservators")}
              </span>
            </div>
            {parkConfiguration?.hasTrainings ? (
              <div className="bg-white rounded-xl p-3 flex flex-col gap-1 relative flex-1">
                <Icon
                  name="FiBox"
                  className="w-6 h-6 absolute top-3 right-3 text-emerald-700/50"
                />
                <span className="text-3xl font-bold text-emerald-700">
                  {balance?.global?.numberOfTrainings ?? 0}
                </span>
                <span className="font-semibold leading-5 text-emerald-900">
                  {t("balance.numberOfTrainings")}
                </span>
              </div>
            ) : null}
            {parkConfiguration?.hasEnrichments ? (
              <div className="bg-white rounded-xl p-3 flex flex-col gap-1 relative flex-1">
                <Icon
                  name="BsStar"
                  className="w-6 h-6 absolute top-3 right-3 text-emerald-700/50"
                />
                <span className="text-3xl font-bold text-emerald-700">
                  {balance?.global?.numberOfEnrichments ?? 0}
                </span>
                <span className="font-semibold leading-5 text-emerald-900">
                  {t("balance.numberOfEnrichments")}
                </span>
              </div>
            ) : null}
            {parkConfiguration?.hasModifications ? (
              <div className="bg-white rounded-xl p-3 flex flex-col gap-1 relative flex-1">
                <Icon
                  name="PencilSquareIcon"
                  className="w-6 h-6 absolute top-3 right-3 text-emerald-700/50"
                />
                <span className="text-3xl font-bold text-emerald-700">
                  {balance?.global?.numberOfEnclosureUpdates ?? 0}
                </span>
                <span className="font-semibold leading-5 text-emerald-900">
                  {t("balance.numberOfEnclosureUpdates")}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {Boolean(
        balance &&
          balance.additionalData &&
          (balance.additionalData.animal ||
            balance.additionalData.observationGroup ||
            balance.additionalData.observationGrid ||
            balance.additionalData.specy ||
            balance.additionalData.superfamily ||
            balance.additionalData.enclosure ||
            balance.additionalData.sector ||
            balance.additionalData.tag ||
            balance.additionalData.assignedUser ||
            balance.additionalData.manager)
      ) && balance?.additionalData ? (
        <div className="mt-2 pl-2">
          <p className="text-xl font-semibold">
            {t("balance.balanceLaunchedOn")}
          </p>
          {balance.additionalData.animal && (
            <div className="flex flex-row">
              <Icon
                name="IoPawOutline"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.animal", {
                  animal:
                    balance.additionalData.animal?.houseName ??
                    balance.additionalData.animal?.localId ??
                    balance.additionalData.animal?.tag?.split("\n")?.[0] ??
                    balance.additionalData.animal?.zimsId,
                })}
              </p>
            </div>
          )}
          {balance.additionalData.observationGroup && (
            <div className="flex flex-row">
              <Icon
                name="EyeIcon"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.observationGroup", {
                  observationGroup:
                    balance.additionalData.observationGroup?.name ?? "",
                })}
              </p>
            </div>
          )}
          {balance.additionalData.observationGrid && (
            <div className="flex flex-row">
              <Icon
                name="TableCellsIcon"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.observationGrid", {
                  observationGrid:
                    balance.additionalData.observationGrid?.name ?? "",
                })}
              </p>
            </div>
          )}
          {balance.additionalData.specy && (
            <div className="flex flex-row">
              <Icon
                name="BsFeather"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.specy", {
                  specy:
                    balance.additionalData.specy.name &&
                    balance.additionalData.specy.scientificName
                      ? `${balance.additionalData.specy.scientificName} (${balance.additionalData.specy.name})`
                      : balance.additionalData.specy.name ??
                        balance.additionalData.specy.scientificName,
                })}
              </p>
            </div>
          )}
          {balance.additionalData.superfamily && (
            <div className="flex flex-row">
              <Icon
                name="GiCheckboxTree"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.superfamily", {
                  superfamily: balance.additionalData.superfamily.name,
                })}
              </p>
            </div>
          )}
          {balance.additionalData.enclosure && (
            <div className="flex flex-row">
              <Icon
                name="TbFence"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.enclosure", {
                  enclosure: balance.additionalData.enclosure.name,
                })}
              </p>
            </div>
          )}
          {balance.additionalData.sector && (
            <div className="flex flex-row">
              <Icon
                name="PiTreePalmLight"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.sector", {
                  sector: balance.additionalData.sector.name,
                })}
              </p>
            </div>
          )}
          {balance.additionalData.tag && (
            <div className="flex flex-row">
              <Icon
                name="PiTagLight"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.tag", {
                  tag: balance.additionalData.tag.name,
                })}
              </p>
            </div>
          )}
          {balance.additionalData.assignedUser && (
            <div className="flex flex-row">
              <Icon
                name="BsStar"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.assignedUser", {
                  assignedUser: balance.additionalData.assignedUser.username,
                })}
              </p>
            </div>
          )}
          {balance.additionalData.manager && (
            <div className="flex flex-row">
              <Icon
                name="BsStarHalf"
                className="w-6 h-6 inline text-emerald-700 mr-2"
              />
              <p>
                {t("balance.manager", {
                  manager: balance.additionalData.manager.username,
                })}
              </p>
            </div>
          )}
        </div>
      ) : null}

      <div className="flex flex-row gap-4 mt-4">
        {balance &&
        Boolean(
          balance?.distribution ||
            balance?.evolution ||
            balance?.evolutionBySection ||
            balance?.comments ||
            balance?.events ||
            balance?.enrichments ||
            balance?.enclosureUpdates
        ) ? (
          <>
            {!!balance?.distribution && (
              <DistributionRadar
                distribution={balance?.distribution}
                comments={balance?.comments}
                isDistributionGraphChartVisible={
                  displayOptions.showDistributionGraph
                }
                isCommentsChartVisible={displayOptions.showCommentsGraph}
                isBadCriteriaChartVisible={displayOptions.showBadCriteriaGraph}
                areEventsVisible={displayOptions.showEvents}
                areEnrichmentsVisible={displayOptions.showEnrichment}
                areUpdatesVisible={displayOptions.showEnclosureUpdate}
                events={balance?.events}
                enrichments={balance?.enrichments}
                enclosureUpdates={balance?.enclosureUpdates}
              />
            )}

            {Boolean(balance?.evolution) && (
              <EvolutionChart
                evolution={balance?.evolution}
                evolutionBySection={balance?.evolutionBySection}
                isEvolutionChartVisible={displayOptions.showEvolutionGraph}
                isSectionChartVisible={displayOptions.showSectionGraph}
                areSubEventsVisible={displayOptions.showSubEventsInGraph}
                areSubEnclosureUpdatesVisible={
                  displayOptions.showSubEnclosureUpdateInGraph
                }
                areSubEnrichmentsVisible={
                  displayOptions.showSubEnrichmentInGraph
                }
              />
            )}
          </>
        ) : (
          <div className="w-full items-center text-center">
            <span>{t(`common.noDataToShow`)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default BalanceSheetReport;
